import React, { useState } from "react";
import {
  CaretWrapper,
  ChangeContainer,
  ChangeSection,
  DateTime,
  FlexDiv,
  HistoryListItem,
  LinkAttr,
  ModifiedText,
  StateColumn,
  StateLabel,
  StateValue,
  SubItemContent,
  SubItemHeader,
} from "./styles";
import { Table } from "../../../../components/Table/Table";
import { ColumnDef } from "@tanstack/react-table";
import { DiffText } from "./styles";
import { CaretDownIcon } from "../../../../components/Icons/Icons";
import styled, { useTheme } from "styled-components";
import { TooltipPortal } from "../../../../components/TooltipModal/TooltipPortal";
import ReactTooltip from "react-tooltip";
import { afterStateLabel, beforeStateLabel } from "./utils";
import { getObjectReferenceDetailPath } from "../../../../components/ObjectReference/ObjectReferencePreviewTooltip";
import { useRoutePath } from "../../../../util/Routing";
import { formatDateTimeWithTime } from "../../../../util/util";
import { EntityPreview, SubsidiaryAction } from "./types";
import { spacingTokens } from "../../../../designTokens";

const EMPTY_VALUE = "-";

const SectionDivider = styled.div`
  display: block;
  width: 100%;
  margin-bottom: ${spacingTokens.spacing_lg};
  border-bottom: ${({ theme }) =>
    `${theme.resolve("border_md")} solid ${theme.secondaryBorder}`};
`;

interface CompanyHierarchyActivityProps {
  parentEntity?: {
    before?: EntityPreview | null;
    after?: EntityPreview | null;
  };
  subsidiaryActions?: {
    before?: SubsidiaryAction[];
    after?: SubsidiaryAction[];
  };
}

interface AttributeRow {
  attribute: string;
  before: string;
  after: string;
  beforeLink?: string | null;
  afterLink?: string | null;
}

function renderCellValue(
  value: string,
  link: string | null | undefined,
  highlight: boolean,
  variant: "added" | "removed"
) {
  const content = link ? <LinkAttr to={link}>{value}</LinkAttr> : value;

  if (variant === "removed") {
    return <DiffText removed={highlight}>{content}</DiffText>;
  }
  return <DiffText added={highlight}>{content}</DiffText>;
}

function EntityTooltipContent({
  entity,
  tooltipId,
}: {
  entity: EntityPreview;
  tooltipId: string;
}) {
  const previewData = entity.preview_attributes ?? entity.preview_rows ?? [];
  const theme = useTheme();
  return (
    <TooltipPortal>
      <ReactTooltip
        id={tooltipId}
        place="top"
        effect="solid"
        backgroundColor={theme.tooltipBG}
      >
        <div>
          {previewData?.map((attribute) => {
            return (
              <div key={attribute.label}>
                <span style={{ opacity: 0.8 }}>{attribute.label}:</span>{" "}
                <span>{attribute.value}</span>
              </div>
            );
          })}
        </div>
      </ReactTooltip>
    </TooltipPortal>
  );
}

interface ActivityTableSectionProps {
  title: string;
  timestamp: string;
  defaultExpanded?: boolean;
  rows: AttributeRow[];
}

export function ActivityTableSection({
  title,
  timestamp,
  rows,
  defaultExpanded = true,
}: ActivityTableSectionProps) {
  const [expanded, setExpanded] = useState(defaultExpanded);

  const columns: ColumnDef<AttributeRow>[] = [
    {
      header: "Attribute Name",
      accessorKey: "attribute",
    },
    {
      header: "Before",
      accessorKey: "before",
      cell: ({ row }) => {
        const { before, after, beforeLink } = row.original;

        if (before === EMPTY_VALUE) {
          return <span>{EMPTY_VALUE}</span>;
        }

        return renderCellValue(
          before,
          beforeLink,
          before !== after && before !== EMPTY_VALUE,
          "removed"
        );
      },
    },
    {
      header: "After",
      accessorKey: "after",
      cell: ({ row }) => {
        const { before, after, afterLink } = row.original;
        if (after === EMPTY_VALUE) {
          return <span>{EMPTY_VALUE}</span>;
        }
        return renderCellValue(
          after,
          afterLink,
          before !== after && after !== EMPTY_VALUE,
          "added"
        );
      },
    },
  ];

  return (
    <>
      <SubItemHeader onClick={() => setExpanded((v) => !v)}>
        <CaretWrapper isExpanded={expanded}>
          <CaretDownIcon width={16} height={16} />
        </CaretWrapper>

        <FlexDiv>
          <ModifiedText>{title}</ModifiedText>

          <DateTime>{formatDateTimeWithTime(timestamp)}</DateTime>
        </FlexDiv>
      </SubItemHeader>

      {expanded && (
        <SubItemContent>
          <Table
            columns={columns}
            data={rows}
            isLoading={false}
            error={undefined}
            enableDefaultSort={false}
            enableColumnResize={false}
            lastChildleftAlign={true}
            cellVerticalAlignTop={true}
          />
        </SubItemContent>
      )}
    </>
  );
}

function SubsidiarySection({ action }: { action: SubsidiaryAction }) {
  const previewData =
    action.subsidiary.preview_attributes ??
    action.subsidiary.preview_rows ??
    [];

  const isRemoval = action.sub_action.toLowerCase().includes("removed");

  const rows: AttributeRow[] = [
    ...(action.subsidiary.object_number
      ? [
          {
            attribute: "Supplier ID",
            before: isRemoval ? action.subsidiary.object_number : EMPTY_VALUE,
            after: isRemoval ? EMPTY_VALUE : action.subsidiary.object_number,
          },
        ]
      : []),
    ...previewData.map((row) => ({
      attribute: row.label ?? row.attribute_name ?? "",
      before: isRemoval ? row.value : EMPTY_VALUE,
      after: isRemoval ? EMPTY_VALUE : row.value,
    })),
  ];

  return (
    <ActivityTableSection
      title={`${action.sub_action}`}
      timestamp={action.timestamp}
      rows={rows}
    />
  );
}

export function CompanyHierarchyReferenceChange({
  beforeItems,
  afterItems,
  attributeName,
}: {
  beforeItems: EntityPreview[];
  afterItems: EntityPreview[];
  attributeName?: string;
}) {
  const { adminPath } = useRoutePath();

  const isRemoved = (item: EntityPreview) =>
    !afterItems.some((x) => x.id === item.id);

  const isAdded = (item: EntityPreview) =>
    !beforeItems.some((x) => x.id === item.id);

  const renderSide = (items: EntityPreview[], side: "before" | "after") => {
    if (!items.length) {
      return EMPTY_VALUE;
    }
    return (
      <ul style={{ listStyle: "inside", margin: 0, padding: 0 }}>
        {items.map((item, index) => {
          const tooltipId = `${side}-${item.id}-${index}`;

          return (
            <HistoryListItem
              key={tooltipId}
              $removed={side === "before" ? isRemoved(item) : false}
              $added={side === "after" ? isAdded(item) : false}
            >
              <LinkAttr
                to={getObjectReferenceDetailPath(
                  adminPath,
                  item.object_type,
                  item.id
                )}
                data-tip
                data-for={tooltipId}
              >
                {item.name}
              </LinkAttr>

              <EntityTooltipContent entity={item} tooltipId={tooltipId} />
            </HistoryListItem>
          );
        })}
      </ul>
    );
  };

  return (
    <ChangeContainer>
      <StateColumn>
        <StateLabel>{beforeStateLabel(attributeName)}</StateLabel>
        <StateValue>{renderSide(beforeItems, "before")}</StateValue>
      </StateColumn>

      <StateColumn>
        <StateLabel>{afterStateLabel(attributeName)}</StateLabel>
        <StateValue>{renderSide(afterItems, "after")}</StateValue>
      </StateColumn>
    </ChangeContainer>
  );
}

export function CompanyHierarchyActivity({
  parentEntity,
  subsidiaryActions,
}: CompanyHierarchyActivityProps) {
  const beforeParent =
    parentEntity?.before && "id" in parentEntity.before
      ? (parentEntity.before as EntityPreview)
      : null;
  const afterParent =
    parentEntity?.after && "id" in parentEntity.after
      ? (parentEntity.after as EntityPreview)
      : null;

  const allSubsidiaryActions = [
    ...(subsidiaryActions?.before ?? []),
    ...(subsidiaryActions?.after ?? []),
  ];

  return (
    <div>
      {(beforeParent || afterParent) && (
        <ChangeSection>
          <StateLabel
            style={{ marginBottom: 10, color: "black", fontSize: "14px" }}
          >
            Parent entity
          </StateLabel>
          <CompanyHierarchyReferenceChange
            beforeItems={beforeParent ? [beforeParent] : []}
            afterItems={afterParent ? [afterParent] : []}
            attributeName="Parent Entity"
          />
        </ChangeSection>
      )}

      {allSubsidiaryActions.length > 0 && (
        <>
          <SectionDivider />
          <StateLabel
            style={{ marginBottom: 10, color: "black", fontSize: "14px" }}
          >
            Subsidiaries
          </StateLabel>
          {allSubsidiaryActions.map((action, index) => (
            <SubsidiarySection
              key={`${action.subsidiary.id}-${index}`}
              action={action}
            />
          ))}
        </>
      )}
    </div>
  );
}
